'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { Raffle } from '@/types/models';

export type UseMainContentProps = {
  raffle?: Raffle;
};

export type UseMainContentReturn = {
  ticketQuantity: number;
  totalValue: number;
  incrementQuantity: () => void;
  decrementQuantity: () => void;
  addQuantity: (amount: number) => void;
  setQuantity: (quantity: number) => void;
  resetQuantity: () => void;
  canIncrement: boolean;
  canDecrement: boolean;
};

export const useMainContent = ({ raffle }: UseMainContentProps): UseMainContentReturn => {
  const [ticketQuantity, setTicketQuantity] = useState(raffle?.initialTicketsAmount || 1);

  useEffect(() => {
    if (raffle?.initialTicketsAmount) {
      setTicketQuantity(raffle.initialTicketsAmount);
    }
  }, [raffle?.initialTicketsAmount]);

  const totalValue = useMemo(() => {
    return ticketQuantity * (raffle?.ticketPrice || 0);
  }, [ticketQuantity, raffle?.ticketPrice]);

  const incrementQuantity = useCallback(() => {
    setTicketQuantity((prev) => {
      const newQuantity = prev + 1;
      return newQuantity <= (raffle?.maxTicketsPerPurchase || 100) ? newQuantity : prev;
    });
  }, [raffle?.maxTicketsPerPurchase]);

  const decrementQuantity = useCallback(() => {
    setTicketQuantity((prev) => {
      const newQuantity = prev - 1;
      return newQuantity >= (raffle?.minTicketsPerPurchase || 1) ? newQuantity : prev;
    });
  }, [raffle?.minTicketsPerPurchase]);

  const addQuantity = useCallback(
    (amount: number) => {
      setTicketQuantity((prev) => {
        const newQuantity = prev + amount;
        const maxTickets = raffle?.maxTicketsPerPurchase || 100;
        const finalQuantity = newQuantity <= maxTickets ? newQuantity : maxTickets;
        return finalQuantity;
      });
    },
    [raffle?.maxTicketsPerPurchase],
  );

  const setQuantity = useCallback(
    (quantity: number) => {
      const minTickets = raffle?.minTicketsPerPurchase || 1;
      const maxTickets = raffle?.maxTicketsPerPurchase || 100;
      if (quantity >= minTickets && quantity <= maxTickets) {
        setTicketQuantity(quantity);
      }
    },
    [raffle?.minTicketsPerPurchase, raffle?.maxTicketsPerPurchase],
  );

  const resetQuantity = useCallback(() => {
    setTicketQuantity(raffle?.initialTicketsAmount || 1);
  }, [raffle?.initialTicketsAmount]);

  return {
    ticketQuantity,
    totalValue,
    incrementQuantity,
    decrementQuantity,
    addQuantity,
    setQuantity,
    resetQuantity,
    canIncrement: ticketQuantity < (raffle?.maxTicketsPerPurchase || 100),
    canDecrement: ticketQuantity > (raffle?.minTicketsPerPurchase || 1),
  };
};
